import CryptoJS from "crypto-js";
import { env } from "@core/config/env";
import { jwtDecode } from "jwt-decode";
import { User, UserRole } from "@core/casl-provider";

const TOKEN_KEY = "bytro-auth-token";
const SECRET_KEY = env.encryptionKey;

// Encryption/Decryption utilities
export const encryptToken = (token: string): string => {
  return CryptoJS.AES.encrypt(token, SECRET_KEY).toString();
};

export const decryptToken = (encryptedToken: string): string | null => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
};

// Get stored token from localstorage
export const getStoredToken = (): string | null => {
  const encryptedToken = localStorage.getItem(TOKEN_KEY);
  if (encryptedToken) {
    return decryptToken(encryptedToken);
  }
  return null;
};

// Store token in localstorage
export const storeToken = (token: string): void => {
  const encryptedToken = encryptToken(token);
  localStorage.setItem(TOKEN_KEY, encryptedToken);
};

// Remove token from localstorage
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Handle JWT expiration and redirect to login
export const handleJwtExpiration = () => {
  removeToken();
  const loginUrl = new URL("/login", window.location.origin);
  loginUrl.searchParams.set("session_expired", "true");
  window.location.href = loginUrl.toString();
  return Promise.reject(new Error("Session expired"));
};

// Get user information from JWT token
export const getUserFromToken = (): User | null => {
  const token = getStoredToken();
  if (!token) return null;

  try {
    const decoded: { [key: string]: unknown } = jwtDecode(token);
    return {
      id: (decoded.sub || decoded.userId || decoded.id) as string,
      role: (decoded.role as UserRole) || "viewer", // Default to viewer if role not specified
      email: decoded.email as string,
      name: (decoded.name || decoded.preferred_username) as string,
      googleId: decoded.googleId as string,
    };
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};
